import React, { useState, useMemo } from "react";
import { LineChart, DualLineChart } from "./charts/TrendCharts";
import S from "../styles";

const MONTH_NAMES_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── Main Revenue Page ────────────────────────────────
export default function RevenuePage({ jobs, progressJobs, expenses }) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // All completed jobs
  const allRev = jobs.reduce((s, j) => s + j.customerCost, 0);
  const allProfit = jobs.reduce((s, j) => s + j.profit, 0);
  const avgMargin = allRev > 0 ? (allProfit / allRev) * 100 : 0;

  // Pipeline
  const pipelineRev = progressJobs.reduce((s, j) => s + j.customerCost, 0);

  // This month
  const currentMonth = new Date().toISOString().slice(0, 7);
  const thisMonthJobs = jobs.filter((j) => j.date && j.date.slice(0, 7) === currentMonth);
  const thisMonthRev = thisMonthJobs.reduce((s, j) => s + j.customerCost, 0);

  // Last month
  const now = new Date();
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonth = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, "0")}`;
  const lastMonthRev = jobs.filter((j) => j.date && j.date.slice(0, 7) === lastMonth).reduce((s, j) => s + j.customerCost, 0);
  const revChange = lastMonthRev > 0 ? ((thisMonthRev - lastMonthRev) / lastMonthRev) * 100 : 0;

  // Monthly data for selected year
  const monthlyData = useMemo(() => {
    return MONTH_NAMES_SHORT.map((label, i) => {
      const mk = `${selectedYear}-${String(i + 1).padStart(2, "0")}`;
      const mJobs = jobs.filter((j) => j.date && j.date.slice(0, 7) === mk);
      const revenue = mJobs.reduce((s, j) => s + j.customerCost, 0);
      const profit = mJobs.reduce((s, j) => s + j.profit, 0);
      const cost = mJobs.reduce((s, j) => s + j.ourCost, 0);
      const count = mJobs.length;
      return { label, month: mk, revenue, profit, cost, count };
    });
  }, [jobs, selectedYear]);

  const yearRevenue = monthlyData.reduce((s, m) => s + m.revenue, 0);
  const yearProfit = monthlyData.reduce((s, m) => s + m.profit, 0);
  const yearCost = monthlyData.reduce((s, m) => s + m.cost, 0);
  const yearJobs = monthlyData.reduce((s, m) => s + m.count, 0);
  const yearMargin = yearRevenue > 0 ? (yearProfit / yearRevenue) * 100 : 0;

  // Expenses for selected year
  const yearExpenses = useMemo(() => {
    return (expenses || []).filter((e) => e.date && e.date.slice(0, 4) === String(selectedYear))
      .reduce((s, e) => s + (parseFloat(e.cost) || 0), 0);
  }, [expenses, selectedYear]);

  // Top customers for selected year
  const topCustomers = useMemo(() => {
    const map = {};
    jobs.filter((j) => j.date && j.date.slice(0, 4) === String(selectedYear)).forEach((j) => {
      if (!map[j.customer]) map[j.customer] = { revenue: 0, profit: 0, count: 0 };
      map[j.customer].revenue += j.customerCost;
      map[j.customer].profit += j.profit;
      map[j.customer].count++;
    });
    return Object.entries(map)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 8);
  }, [jobs, selectedYear]);

  // Monthly expenses overlay
  const monthlyExpenses = useMemo(() => {
    return MONTH_NAMES_SHORT.map((label, i) => {
      const mk = `${selectedYear}-${String(i + 1).padStart(2, "0")}`;
      const total = (expenses || []).filter((e) => e.date && e.date.slice(0, 7) === mk)
        .reduce((s, e) => s + (parseFloat(e.cost) || 0), 0);
      return { label, total };
    });
  }, [expenses, selectedYear]);

  // Revenue by job type
  const byJobType = useMemo(() => {
    const map = {};
    const JOB_TYPE_COLORS = { "Laser Cutting": "#ef4444", "3D Printing": "#8b5cf6", "UV Printing": "#3b82f6", CNC: "#f59e0b" };
    jobs.filter((j) => j.date && j.date.slice(0, 4) === String(selectedYear)).forEach((j) => {
      const t = j.jobType || "Other";
      if (!map[t]) map[t] = { revenue: 0, profit: 0, cost: 0, count: 0, color: JOB_TYPE_COLORS[t] || "rgba(10,10,15,0.58)" };
      map[t].revenue += j.customerCost;
      map[t].profit += j.profit;
      map[t].cost += j.ourCost;
      map[t].count++;
    });
    return Object.entries(map).map(([type, d]) => ({ type, ...d })).sort((a, b) => b.revenue - a.revenue);
  }, [jobs, selectedYear]);

  const fmtK = (v) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toLocaleString()}`;
  const fmtFull = (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>Revenue Dashboard</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Track revenue, profit, and financial performance</p>
      </div>

      {/* Top-level stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}>
          <span style={S.statLabel}>Lifetime Revenue</span>
          <span style={{ ...S.statValue, color: "#0033FF" }}>{fmtFull(allRev)}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Lifetime Profit</span>
          <span style={{ ...S.statValue, color: "#34d399" }}>{fmtFull(allProfit)}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Avg Margin</span>
          <span style={S.statValue}>{avgMargin.toFixed(1)}%</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>This Month</span>
          <span style={{ ...S.statValue, color: "#0033FF" }}>{fmtFull(thisMonthRev)}</span>
          {lastMonthRev > 0 && (
            <span style={{ fontSize: 13, color: revChange >= 0 ? "#34d399" : "#ef4444", fontFamily: "'JetBrains Mono', monospace" }}>
              {revChange >= 0 ? "+" : ""}{revChange.toFixed(0)}% vs last month
            </span>
          )}
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Pipeline</span>
          <span style={S.statValue}>{fmtFull(pipelineRev)}</span>
          <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{progressJobs.length} open job{progressJobs.length !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Year selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
        <button onClick={() => setSelectedYear((y) => y - 1)} style={{ ...S.iconBtn, padding: "6px 10px", fontSize: 16 }}>‹</button>
        <span style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F", minWidth: 60, textAlign: "center" }}>{selectedYear}</span>
        <button onClick={() => setSelectedYear((y) => y + 1)} style={{ ...S.iconBtn, padding: "6px 10px", fontSize: 16 }}>›</button>
        <span style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginLeft: 12 }}>{yearJobs} jobs completed</span>
      </div>

      {/* Year stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}>
          <span style={S.statLabel}>{selectedYear} Revenue</span>
          <span style={{ ...S.statValue, color: "#0033FF" }}>{fmtFull(yearRevenue)}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>{selectedYear} Profit</span>
          <span style={{ ...S.statValue, color: "#34d399" }}>{fmtFull(yearProfit)}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>{selectedYear} Costs</span>
          <span style={S.statValue}>{fmtFull(yearCost)}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>{selectedYear} Expenses</span>
          <span style={S.statValue}>{fmtFull(yearExpenses)}</span>
        </div>
        <div style={S.statCard}>
          <span style={S.statLabel}>{selectedYear} Margin</span>
          <span style={S.statValue}>{yearMargin.toFixed(1)}%</span>
        </div>
      </div>

      {/* Charts */}
      <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Revenue vs Profit chart */}
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>Revenue vs Profit</h3>
            <div style={{ display: "flex", gap: 12 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "rgba(10,10,15,0.58)" }}>
                <span style={{ width: 8, height: 8, borderRadius: 0, background: "#0033FF" }} /> Revenue
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "rgba(10,10,15,0.58)" }}>
                <span style={{ width: 8, height: 8, borderRadius: 0, background: "#34d399" }} /> Profit
              </span>
            </div>
          </div>
          <DualLineChart data={monthlyData} />
        </div>

        {/* Expenses chart */}
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, overflow: "hidden" }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)", marginBottom: 16 }}>Monthly Expenses</h3>
          <LineChart data={monthlyExpenses} valueKey="total" labelKey="label" color="#ef4444" formatValue={fmtK} />
        </div>
      </div>

      {/* Top customers */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 20px", background: "#FFFFFF", borderBottom: "2px solid #0A0A0F" }}>
            <span style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)" }}>Top Customers — {selectedYear}</span>
          </div>
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {topCustomers.length > 0 ? topCustomers.map((c, i) => {
              const pct = topCustomers[0].revenue > 0 ? (c.revenue / topCustomers[0].revenue) * 100 : 0;
              return (
                <div key={c.name} style={{ padding: "12px 20px", borderBottom: "1px solid rgba(10,10,15,0.12)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)", width: 20 }}>{i + 1}.</span>
                      <span style={{ fontSize: 14, fontWeight: 500, color: "#0A0A0F" }}>{c.name}</span>
                      <span style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", background: "#F4F2EC", padding: "1px 8px", borderRadius: 0}}>{c.count} job{c.count !== 1 ? "s" : ""}</span>
                    </div>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 600, color: "#0033FF" }}>{fmtFull(c.revenue)}</span>
                  </div>
                  <div style={{ height: 4, background: "#FFFFFF", borderRadius: 0, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: "#0033FF", borderRadius: 0, transition: "width .4s" }} />
                  </div>
                </div>
              );
            }) : (
              <div style={{ padding: 30, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>No customers in {selectedYear}.</div>
            )}
          </div>
        </div>
      </div>

      {/* Revenue by Job Type */}
      {yearRevenue > 0 && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)", marginBottom: 14 }}>{selectedYear} Revenue by Service</h3>
          {/* Stacked bar */}
          <div style={{ display: "flex", height: 32, borderRadius: 0, overflow: "hidden", marginBottom: 16 }}>
            {byJobType.map((t) => (
              <div key={t.type} style={{ width: `${(t.revenue / yearRevenue) * 100}%`, background: t.color, transition: "width .4s", minWidth: t.revenue > 0 ? 2 : 0 }} title={`${t.type}: ${fmtFull(t.revenue)}`} />
            ))}
          </div>
          {/* Detail rows */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {byJobType.map((t) => {
              const pct = yearRevenue > 0 ? (t.revenue / yearRevenue) * 100 : 0;
              const profitMargin = t.revenue > 0 ? (t.profit / t.revenue) * 100 : 0;
              return (
                <div key={t.type} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 140 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 0, background: t.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F" }}>{t.type}</span>
                  </div>
                  <div style={{ flex: 1, height: 6, background: "#FFFFFF", borderRadius: 0, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: t.color, borderRadius: 0, transition: "width .3s" }} />
                  </div>
                  <div style={{ display: "flex", gap: 16, alignItems: "center", minWidth: 340, justifyContent: "flex-end" }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "#0A0A0F", fontWeight: 600 }}>{fmtFull(t.revenue)}</span>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#34d399" }}>+{fmtFull(t.profit)}</span>
                    <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", background: "#F4F2EC", padding: "1px 8px", borderRadius: 0, minWidth: 42, textAlign: "center" }}>{pct.toFixed(0)}%</span>
                    <span style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>{t.count} job{t.count !== 1 ? "s" : ""}</span>
                    <span style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>{profitMargin.toFixed(0)}% margin</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
